import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ClipboardCheck, ArrowLeft, Send, CheckCircle, ImagePlus, Eye, X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BulkImageUploadDialog } from '@/components/BulkImageUploadDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyEntry = {
  patient_name: '', age: '', gender: '', procedure_type: '', diagnosis: '', treatment_plan: '',
  technique: '', products: '', complication: false, management_protocol: '', outcome: '', supervisor_validation: ''
};

export default function LogbookPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ ...emptyEntry });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [beforeImageFile, setBeforeImageFile] = useState(null);
  const [afterImageFile, setAfterImageFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [viewImagesEntry, setViewImagesEntry] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const minRequired = 5;

  useEffect(() => {
    fetchEntries();
    fetchSubmissionStatus();
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/logbook/entries`, { headers: { Authorization: `Bearer ${token}` } });
      setEntries(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchSubmissionStatus = async () => {
    try {
      const res = await axios.get(`${API}/my-case-logs`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.length > 0) {
        setSubmissionStatus(res.data[0]);
      }
    } catch (err) { console.error(err); }
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a JPG, PNG, or WebP image');
      e.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10 MB');
      e.target.value = '';
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    if (type === 'before') {
      setBeforeImageFile(file);
      setBeforePreview(previewUrl);
    } else {
      setAfterImageFile(file);
      setAfterPreview(previewUrl);
    }
  };

  const clearImageSelection = (type) => {
    if (type === 'before') {
      if (beforePreview) URL.revokeObjectURL(beforePreview);
      setBeforeImageFile(null);
      setBeforePreview(null);
    } else {
      if (afterPreview) URL.revokeObjectURL(afterPreview);
      setAfterImageFile(null);
      setAfterPreview(null);
    }
  };

  const uploadImage = async (entryId, file, imageType) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(
      `${API}/logbook/entries/${entryId}/images?image_type=${imageType}`,
      formData,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
    );
    return res.data;
  };

  const resetFormAndImages = () => {
    setForm({ ...emptyEntry });
    clearImageSelection('before');
    clearImageSelection('after');
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/logbook/entries`, { ...form, age: parseInt(form.age) || 0 }, { headers: { Authorization: `Bearer ${token}` } });
      const newEntryId = res.data.id;

      // Upload images if selected
      if (beforeImageFile || afterImageFile) {
        setUploadingImages(true);
        try {
          if (beforeImageFile) await uploadImage(newEntryId, beforeImageFile, 'before');
          if (afterImageFile) await uploadImage(newEntryId, afterImageFile, 'after');
          toast.success('Case entry added with images');
        } catch (imgErr) {
          console.error('Image upload failed:', imgErr);
          toast.warning('Case entry saved, but image upload failed. You can re-upload later.');
        } finally {
          setUploadingImages(false);
        }
      } else {
        toast.success('Case entry added');
      }

      resetFormAndImages();
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add entry');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/logbook/entries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleSubmitForReview = async () => {
    if (entries.length < minRequired) {
      toast.error(`You need at least ${minRequired} cases to submit for review`);
      return;
    }
    
    try {
      setLoading(true);
      await axios.post(`${API}/case-logs`, {
        title: 'Clinical Case Logbook Submission',
        total_cases: entries.length,
        entry_ids: entries.map(e => e.id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Logbook submitted for examiner/admin review!');
      fetchSubmissionStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit logbook');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="logbook-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')} 
            className="text-white hover:bg-white/10 mb-4 -ml-2"
            data-testid="logbook-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
          </Button>
          <p className="imcac-overline mb-3">Clinical Documentation</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Online Logbook</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
        </div>
      </section>

      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          {/* Progress Counter */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="h-5 w-5 text-[#C5A572]" />
              <span className="text-sm text-slate-700">
                <strong className="text-[#1B2A4A]">{entries.length}</strong> / {minRequired} minimum required cases
              </span>
              <Badge className={`text-xs rounded-sm ${entries.length >= minRequired ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid="cases-badge">
                {entries.length >= minRequired ? 'Requirement Met' : `${minRequired - entries.length} more needed`}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowBulkUpload(true)}
                disabled={entries.length === 0}
                className="rounded-sm border-[#C5A572] text-[#C5A572] hover:bg-[#C5A572]/10"
                data-testid="bulk-upload-btn"
              >
                <ImagePlus className="h-4 w-4 mr-2" /> Bulk Upload Images
              </Button>
              <Button onClick={() => setShowForm(!showForm)} className="bg-[#1B2A4A] text-white rounded-sm" data-testid="add-entry-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Case Entry
              </Button>
            </div>
          </div>

          {/* Submission Status */}
          {submissionStatus ? (
            <Card className="mb-6 border-2 border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-green-700">Logbook Submitted for Review</p>
                      <p className="text-sm text-green-600">
                        {submissionStatus.total_cases} cases • Submitted on {new Date(submissionStatus.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Badge className={`${
                    submissionStatus.status === 'scored' ? 'bg-green-100 text-green-700' :
                    submissionStatus.status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {submissionStatus.status === 'scored' ? 'Reviewed' : 
                     submissionStatus.status === 'assigned' ? 'Under Review' : 'Pending Assignment'}
                  </Badge>
                </div>
                {submissionStatus.scoring && (
                  <div className="mt-3 p-3 bg-white rounded-sm">
                    <p className="text-sm font-medium text-slate-700">Score: <span className="text-[#C5A572]">{submissionStatus.scoring.overall_score}/10</span></p>
                    {submissionStatus.scoring.feedback && (
                      <p className="text-sm text-slate-500 mt-1">Feedback: {submissionStatus.scoring.feedback}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : entries.length >= minRequired && (
            <Card className="mb-6 border-2 border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-amber-700">Ready to Submit!</p>
                    <p className="text-sm text-amber-600">You have met the minimum case requirement. Submit your logbook for examiner/admin review.</p>
                  </div>
                  <Button 
                    onClick={handleSubmitForReview} 
                    disabled={loading}
                    className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm"
                    data-testid="submit-for-review-btn"
                  >
                    <Send className="h-4 w-4 mr-2" /> Submit for Review
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add Entry Form */}
          {showForm && (
            <Card className="imcac-card mb-6" data-testid="logbook-form">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-4">New Case Entry</h3>
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Patient Name *</Label>
                    <Input type="text" value={form.patient_name} onChange={e => setForm({...form, patient_name: e.target.value})} required className="rounded-sm mt-1" data-testid="logbook-patient-name" placeholder="Enter patient name" />
                  </div>
                  <div>
                    <Label className="text-sm">Patient Age *</Label>
                    <Input type="number" value={form.age} onChange={e => setForm({...form, age: e.target.value})} required className="rounded-sm mt-1" data-testid="logbook-age" />
                  </div>
                  <div>
                    <Label className="text-sm">Patient Gender</Label>
                    <Select value={form.gender} onValueChange={v => setForm({...form, gender: v})}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="logbook-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Procedure Type</Label>
                    <Input value={form.procedure_type} onChange={e => setForm({...form, procedure_type: e.target.value})} required placeholder="e.g., Botulinum Toxin Injection" className="rounded-sm mt-1" data-testid="logbook-procedure" />
                  </div>
                  <div>
                    <Label className="text-sm">Diagnosis</Label>
                    <Input value={form.diagnosis} onChange={e => setForm({...form, diagnosis: e.target.value})} required className="rounded-sm mt-1" data-testid="logbook-diagnosis" />
                  </div>
                  <div>
                    <Label className="text-sm">Treatment Plan</Label>
                    <Input value={form.treatment_plan} onChange={e => setForm({...form, treatment_plan: e.target.value})} required className="rounded-sm mt-1" data-testid="logbook-treatment" />
                  </div>
                  <div>
                    <Label className="text-sm">Technique Used</Label>
                    <Input value={form.technique} onChange={e => setForm({...form, technique: e.target.value})} required className="rounded-sm mt-1" data-testid="logbook-technique" />
                  </div>
                  <div>
                    <Label className="text-sm">Products Used</Label>
                    <Input value={form.products} onChange={e => setForm({...form, products: e.target.value})} required className="rounded-sm mt-1" data-testid="logbook-products" />
                  </div>
                  <div>
                    <Label className="text-sm">Outcome</Label>
                    <Input value={form.outcome} onChange={e => setForm({...form, outcome: e.target.value})} required placeholder="e.g., Satisfactory" className="rounded-sm mt-1" data-testid="logbook-outcome" />
                  </div>
                  <div className="flex items-center gap-2 mt-6">
                    <Checkbox checked={form.complication} onCheckedChange={v => setForm({...form, complication: v})} data-testid="logbook-complication" />
                    <Label className="text-sm">Complication Occurred</Label>
                  </div>
                  {form.complication && (
                    <div>
                      <Label className="text-sm">Management Protocol</Label>
                      <Input value={form.management_protocol} onChange={e => setForm({...form, management_protocol: e.target.value})} className="rounded-sm mt-1" data-testid="logbook-management" />
                    </div>
                  )}

                  {/* Before/After Image Uploads */}
                  <div className="md:col-span-2">
                    <div className="border-t border-slate-200 pt-4 mt-2">
                      <Label className="text-sm font-semibold text-[#1B2A4A] flex items-center gap-2 mb-3">
                        <ImagePlus className="h-4 w-4 text-[#C5A572]" /> Before & After Images <span className="text-xs font-normal text-slate-400">(Optional)</span>
                      </Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Before Image */}
                        <div data-testid="before-image-upload-area">
                          <Label className="text-xs text-slate-500 mb-1.5 block">Before Treatment</Label>
                          {beforePreview ? (
                            <div className="relative group rounded-sm overflow-hidden border border-slate-200">
                              <img src={beforePreview} alt="Before preview" className="w-full h-36 object-cover" />
                              <button
                                type="button"
                                onClick={() => clearImageSelection('before')}
                                className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid="clear-before-image-btn"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                                <span className="text-xs text-white truncate block">{beforeImageFile?.name}</span>
                              </div>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center h-36 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-[#C5A572] hover:bg-slate-50 transition-colors">
                              <Upload className="h-6 w-6 text-slate-400 mb-1" />
                              <span className="text-xs text-slate-500">Click to upload</span>
                              <span className="text-[10px] text-slate-400 mt-0.5">JPG, PNG, WebP (max 10MB)</span>
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => handleFileSelect(e, 'before')}
                                className="hidden"
                                data-testid="before-image-input"
                              />
                            </label>
                          )}
                        </div>

                        {/* After Image */}
                        <div data-testid="after-image-upload-area">
                          <Label className="text-xs text-slate-500 mb-1.5 block">After Treatment</Label>
                          {afterPreview ? (
                            <div className="relative group rounded-sm overflow-hidden border border-slate-200">
                              <img src={afterPreview} alt="After preview" className="w-full h-36 object-cover" />
                              <button
                                type="button"
                                onClick={() => clearImageSelection('after')}
                                className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid="clear-after-image-btn"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                                <span className="text-xs text-white truncate block">{afterImageFile?.name}</span>
                              </div>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center h-36 border-2 border-dashed border-slate-300 rounded-sm cursor-pointer hover:border-[#C5A572] hover:bg-slate-50 transition-colors">
                              <Upload className="h-6 w-6 text-slate-400 mb-1" />
                              <span className="text-xs text-slate-500">Click to upload</span>
                              <span className="text-[10px] text-slate-400 mt-0.5">JPG, PNG, WebP (max 10MB)</span>
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => handleFileSelect(e, 'after')}
                                className="hidden"
                                data-testid="after-image-input"
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 flex gap-3">
                    <Button type="submit" disabled={loading || uploadingImages} className="bg-[#C5A572] hover:bg-[#B09260] text-white rounded-sm" data-testid="logbook-submit">
                      {uploadingImages ? 'Uploading images...' : loading ? 'Saving...' : 'Save Entry'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => resetFormAndImages()} className="rounded-sm" data-testid="logbook-cancel">Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Entries Table */}
          <Card className="imcac-card" data-testid="logbook-table">
            <CardContent className="p-0">
              {entries.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No logbook entries yet. Start documenting your cases.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F8FAFC]">
                        <TableHead className="text-xs font-bold text-slate-600">Case ID</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600">Procedure</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600">Patient</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600">Outcome</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600">Images</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600">Complication</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600">Date</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600 w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-xs font-mono text-slate-600">{entry.case_id}</TableCell>
                          <TableCell className="text-sm text-slate-700">{entry.procedure_type}</TableCell>
                          <TableCell className="text-sm text-slate-600">
                            <div>
                              <span className="font-medium">{entry.patient_name || 'N/A'}</span>
                              <span className="text-xs text-slate-400 ml-2">({entry.age}y, {entry.gender})</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{entry.outcome}</TableCell>
                          <TableCell>
                            {(entry.before_image || entry.after_image) ? (
                              <button
                                onClick={() => setViewImagesEntry(entry)}
                                className="flex items-center gap-1 text-[#C5A572] hover:text-[#B09260] transition-colors text-xs font-medium"
                                data-testid={`view-images-${entry.id}`}
                              >
                                <Eye className="h-3.5 w-3.5" />
                                {entry.before_image && entry.after_image ? '2' : '1'}
                              </button>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-xs rounded-sm ${entry.complication ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {entry.complication ? 'Yes' : 'No'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{new Date(entry.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <button onClick={() => handleDelete(entry.id)} className="text-slate-400 hover:text-red-600 transition-colors" data-testid={`delete-entry-${entry.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Bulk Image Upload Dialog */}
      <BulkImageUploadDialog
        open={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        entries={entries}
        token={token}
        onUploaded={fetchEntries}
      />

      {/* Image Viewer Dialog */}
      <Dialog open={!!viewImagesEntry} onOpenChange={() => setViewImagesEntry(null)}>
        <DialogContent className="max-w-2xl" data-testid="image-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] font-heading">
              Case Images — {viewImagesEntry?.case_id}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Before Treatment</p>
              {viewImagesEntry?.before_image ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${viewImagesEntry.before_image}`}
                  alt="Before treatment"
                  className="w-full rounded-sm border border-slate-200 object-cover max-h-72"
                  data-testid="before-image-display"
                />
              ) : (
                <div className="h-48 flex items-center justify-center bg-slate-100 rounded-sm border border-slate-200" data-testid="no-before-image">
                  <span className="text-xs text-slate-400">No before image</span>
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">After Treatment</p>
              {viewImagesEntry?.after_image ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${viewImagesEntry.after_image}`}
                  alt="After treatment"
                  className="w-full rounded-sm border border-slate-200 object-cover max-h-72"
                  data-testid="after-image-display"
                />
              ) : (
                <div className="h-48 flex items-center justify-center bg-slate-100 rounded-sm border border-slate-200" data-testid="no-after-image">
                  <span className="text-xs text-slate-400">No after image</span>
                </div>
              )}
            </div>
          </div>
          {viewImagesEntry && (
            <div className="mt-3 text-xs text-slate-500 border-t pt-3">
              <span className="font-medium text-slate-700">{viewImagesEntry.procedure_type}</span> — {viewImagesEntry.patient_name}, {viewImagesEntry.outcome}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
